/**
 * Card component for content containers.
 *
 * @module components/ui/Card
 */

import { HTMLAttributes, ReactNode } from 'react';
import { clsx } from 'clsx';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/**
 * Card container component
 */
export function Card({ children, className, ...props }: CardProps) {
  return (
    <div
      className={clsx(
        'bg-white dark:bg-[#0a0a0a] rounded-xl shadow-xs border border-gray-100 dark:border-[#2a2a2a] overflow-hidden',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode;
  description?: string;
  action?: ReactNode;
  icon?: ReactNode;
  children?: ReactNode;
}

/**
 * Card header component
 */
export function CardHeader({
  title,
  description,
  action,
  icon,
  children,
  className,
  ...props
}: CardHeaderProps) {
  return (
    <div
      className={clsx(
        'px-6 py-4 border-b border-gray-100 dark:border-[#2a2a2a] flex items-center justify-between',
        className
      )}
      {...props}
    >
      <div className="flex items-start gap-3">
        {icon && <div className="text-gray-500 dark:text-gray-400 mt-0.5">{icon}</div>}
        <div>
          {title && (
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
          )}
          {description && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
          )}
          {children}
        </div>
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

export interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  noPadding?: boolean;
}

/**
 * Card body component
 */
export function CardBody({ children, noPadding = false, className, ...props }: CardBodyProps) {
  return (
    <div className={clsx(!noPadding && 'p-6', className)} {...props}>
      {children}
    </div>
  );
}

export interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/**
 * Card footer component
 */
export function CardFooter({ children, className, ...props }: CardFooterProps) {
  return (
    <div
      className={clsx(
        'px-6 py-4 bg-gray-50 dark:bg-[#161616] border-t border-gray-100 dark:border-[#2a2a2a]',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}
